// Predefined color palette with background and foreground colors
const colors = [
  // ── Reds / Corals ────────────────────────────────
  { bg: '#D32F2F', fg: '#FFFFFF' }, // Crimson
  { bg: '#FF6B6B', fg: '#000000' }, // Red
  { bg: '#F1948A', fg: '#000000' }, // Coral
  { bg: '#F5B7B1', fg: '#000000' }, // Light coral
  { bg: '#FADBD8', fg: '#000000' }, // Misty rose

  // ── Oranges & Yellows ───────────────────────────
  { bg: '#EF6C00', fg: '#000000' }, // Burnt orange
  { bg: '#F8C471', fg: '#000000' }, // Orange
  { bg: '#FAD7A0', fg: '#000000' }, // Peach
  { bg: '#F9E79F', fg: '#000000' }, // Light yellow
  { bg: '#FEF9E7', fg: '#000000' }, // Cream

  // ── Greens / Teals ──────────────────────────────
  { bg: '#0E6251', fg: '#FFFFFF' }, // Deep teal
  { bg: '#4ECDC4', fg: '#000000' }, // Teal
  { bg: '#82E0AA', fg: '#000000' }, // Green
  { bg: '#A9DFBF', fg: '#000000' }, // Mint
  { bg: '#D5F4E6', fg: '#000000' }, // Mint cream

  // ── Blues ───────────────────────────────────────
  { bg: '#1565C0', fg: '#FFFFFF' }, // Indigo blue
  { bg: '#45B7D1', fg: '#000000' }, // Cyan-blue
  { bg: '#85C1E9', fg: '#000000' }, // Sky blue
  { bg: '#AED6F1', fg: '#000000' }, // Powder blue
  { bg: '#A3E4D7', fg: '#000000' }, // Aqua tint

  // ── Purples / Violets ───────────────────────────
  { bg: '#6A1B9A', fg: '#FFFFFF' }, // Royal purple
  { bg: '#BB8FCE', fg: '#000000' }, // Plum
  { bg: '#D7BDE2', fg: '#000000' }, // Lavender
  { bg: '#D2B4DE', fg: '#000000' }, // Thistle
  { bg: '#D5A6BD', fg: '#000000' } // Rose-lilac
];

/**
 * Converts hex color to HSL
 * @param hex - Hex color string (e.g., "#FF0000")
 * @returns HSL object with h, s, l properties
 */
export function hexToHsl(hex: string): { h: number; s: number; l: number } {
  // Remove # if present
  hex = hex.replace('#', '');

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

/**
 * Converts HSL to hex color
 * @param h - Hue (0-360)
 * @param s - Saturation (0-100)
 * @param l - Lightness (0-100)
 * @returns Hex color string
 */
export function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (0 <= h && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (120 <= h && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (180 <= h && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (240 <= h && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (300 <= h && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  const rHex = Math.round((r + m) * 255)
    .toString(16)
    .padStart(2, '0');
  const gHex = Math.round((g + m) * 255)
    .toString(16)
    .padStart(2, '0');
  const bHex = Math.round((b + m) * 255)
    .toString(16)
    .padStart(2, '0');

  return `#${rHex}${gHex}${bHex}`;
}

/**
 * Adjusts the lightness of a color while preserving hue and saturation
 * @param hex - Hex color string
 * @param lightness - Target lightness (0-100)
 * @returns Hex color string with adjusted lightness
 */
export function adjustLightness(hex: string, lightness: number): string {
  const hsl = hexToHsl(hex);
  return hslToHex(hsl.h, hsl.s, Math.max(0, Math.min(100, lightness)));
}

/**
 * Converts a probability to logit (log odds)
 * @param p - Probability between 0 and 1
 * @returns Logit value
 */
export function logit(p: number): number {
  return Math.log(p / (1 - p));
}

/**
 * Converts logit to probability
 * @param logit - Logit value
 * @returns Probability between 0 and 1
 */
export function sigmoid(logit: number): number {
  return 1 / (1 + Math.exp(-logit));
}

/**
 * Applies a relative lightness boost using logit transformation
 * @param originalLightness - Original lightness (0-100)
 * @param boostFactor - Boost factor (e.g., 1.5 for 50% boost, 2.0 for 100% boost)
 * @returns New lightness value (0-100)
 */
export function applyRelativeLightnessBoost(
  originalLightness: number,
  boostFactor: number
): number {
  // Handle edge cases
  if (originalLightness <= 0) return 0;
  if (originalLightness >= 100) return 100;

  // Convert lightness to probability (0-1)
  const p = originalLightness / 100;

  // Clamp probability to avoid logit issues at extremes
  const clampedP = Math.max(0.001, Math.min(0.999, p));

  // Apply logit transformation
  const logitValue = logit(clampedP);

  // For boost factors > 1, we want to make it lighter (increase probability)
  // For boost factors < 1, we want to make it darker (decrease probability)
  // So we need to invert the relationship
  const adjustedLogit = logitValue / boostFactor;

  // Apply bounds to prevent extreme values
  const maxLogit = 10;
  const boundedLogit = Math.max(-maxLogit, Math.min(maxLogit, adjustedLogit));

  // Convert back to probability
  const boostedP = sigmoid(boundedLogit);

  return Math.max(0, Math.min(100, boostedP * 100));
}

/**
 * Gets the foreground color for a given background color from the predefined color palette
 * @param backgroundColor - The background color in hex format (e.g., "#FF0000")
 * @returns The foreground color in hex format (e.g., "#000000" or "#FFFFFF")
 */
export function getForegroundColor(backgroundColor: string): string {
  const colorObj = colors.find((color: { bg: string; fg: string }) => color.bg === backgroundColor);
  return colorObj?.fg || '#000000'; // Default to black if color not found
}

/**
 * Gets the complete color object (background and foreground) for a given background color
 * @param backgroundColor - The background color in hex format
 * @returns The color object with bg and fg properties, or null if not found
 */
export function getColorObject(backgroundColor: string): { bg: string; fg: string } | null {
  return colors.find((color: { bg: string; fg: string }) => color.bg === backgroundColor) || null;
}

/**
 * Gets all available colors
 * @returns Array of color objects with bg and fg properties
 */
export function getAllColors(): Array<{ bg: string; fg: string }> {
  return colors;
}

/**
 * Generates workspace color variants for calendar events
 * @param workspaceColor - The base workspace color in hex format
 * @param isDarkMode - Whether dark mode is enabled (optional, will auto-detect if not provided)
 * @returns Object with gentle, medium, and strong color variants
 */
export function getWorkspaceColorVariants(workspaceColor: string | null, isDarkMode?: boolean): {
  gentleBgColor: string;
  mediumBgColor: string;
  strongBgColor: string;
  gentleFgColor: string;
  mediumFgColor: string;
  strongFgColor: string;
} {
  // Auto-detect dark mode if not explicitly provided
  if (isDarkMode === undefined) {
    isDarkMode = typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  if (!workspaceColor) {
    // Return CSS variables that will adapt to dark mode
    return {
      gentleBgColor: 'var(--cal-event-gentle-bg, #F9FAFB)',
      mediumBgColor: 'var(--cal-event-medium-bg, #E5E7EB)',
      strongBgColor: 'var(--cal-event-strong-bg, #6B7280)',
      gentleFgColor: 'var(--cal-event-gentle-fg, #6B7280)',
      mediumFgColor: 'var(--cal-event-medium-fg, #FFFFFF)',
      strongFgColor: 'var(--cal-event-strong-fg, #FFFFFF)'
    };
  }

  // Convert to HSL for easier manipulation
  const hsl = hexToHsl(workspaceColor);
  const fgColor = getForegroundColor(workspaceColor);
  const isDarkColor = fgColor === '#FFFFFF';

  // Use logit-based relative adjustments
  const strongLightness = applyRelativeLightnessBoost(hsl.l, 0.7); // 30% reduction

  const mediumBgColor = hslToHex(hsl.h, hsl.s, hsl.l);
  const strongBgLightness = Math.min(strongLightness, 60);

  // Adjust lightness values based on theme
  let gentleBgLightness: number;
  let gentleFgLightnessLB: number;

  if (isDarkMode) {
    // Dark mode: Use darker, more subdued backgrounds
    gentleBgLightness = Math.min(30, applyRelativeLightnessBoost(hsl.l, 0.6)); // Much darker
    gentleFgLightnessLB = Math.max(60, Math.min(gentleBgLightness + 40, 80)); // Lighter text
  } else {
    // Light mode: Original bright backgrounds
    gentleBgLightness = Math.max(90, applyRelativeLightnessBoost(hsl.l, 1.5));
    gentleFgLightnessLB = Math.max(20, Math.min(gentleBgLightness - 40, 50));
  }

  const gentleFgLightness = isDarkMode
    ? gentleFgLightnessLB
    : Math.min(hsl.l, gentleFgLightnessLB);
  const strongBgLightnessUB = Math.max(gentleFgLightness - 30, 20);

  if (!isDarkColor) {
    return {
      gentleBgColor: hslToHex(hsl.h, hsl.s, gentleBgLightness),
      mediumBgColor,
      strongBgColor: hslToHex(hsl.h, hsl.s, Math.max(strongBgLightnessUB, strongBgLightness)),
      gentleFgColor: hslToHex(hsl.h, hsl.s, gentleFgLightness),
      mediumFgColor: hslToHex(hsl.h, hsl.s, isDarkMode ? 90 : applyRelativeLightnessBoost(hsl.l, 1.5)),
      strongFgColor: '#FFFFFF'
    };
  } else {
    // For dark base colors, strongBgColor should be dark enough to contrast
    // against the light gentleBgColor background (lightness ~25%)
    const darkStrongLightness = Math.max(20, Math.min(30, hsl.l * 0.5));
    return {
      gentleBgColor: hslToHex(hsl.h, hsl.s, isDarkMode ? Math.min(35, applyRelativeLightnessBoost(hsl.l, 0.8)) : applyRelativeLightnessBoost(hsl.l, 1.5)),
      mediumBgColor,
      strongBgColor: hslToHex(hsl.h, hsl.s, darkStrongLightness),
      gentleFgColor: hslToHex(hsl.h, hsl.s, isDarkMode ? 75 : 90),
      mediumFgColor: hslToHex(hsl.h, hsl.s, isDarkMode ? 85 : 95),
      strongFgColor: hslToHex(hsl.h, hsl.s, 100) // should create white
    };
  }
}
